// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { isNullOrUndefined } from '@shared/utils';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

const isBlank = (value: unknown): boolean => {
  if (isNullOrUndefined(value)) {
    return true;
  }

  if (typeof value === 'string' && value.trim() === '') {
    return true;
  }

  return false;
};

/** Excel serial date → UTC day (SheetJS / Excel 1900 system). */
const excelSerialToDayjs = (serial: number) =>
  dayjs(new Date(Math.round((serial - 25569) * 86400 * 1000)));

export const normalizeExcelText = (value: unknown): string | undefined => {
  if (isBlank(value)) {
    return undefined;
  }

  return String(value).trim();
};

export const normalizeExcelNumber = (value: unknown): number | undefined => {
  if (isBlank(value)) {
    return undefined;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  const text = String(value).trim().replace(/\s/g, '');

  // Support both `1.234,56` and `1,234.56` lightly: prefer last separator as decimal.
  const normalized = (() => {
    const comma = text.lastIndexOf(',');

    const dot = text.lastIndexOf('.');

    if (comma > -1 && dot > -1) {
      if (comma > dot) {
        return text.replace(/\./g, '').replace(',', '.');
      }

      return text.replace(/,/g, '');
    }

    if (comma > -1) {
      return text.replace(',', '.');
    }

    return text;
  })();

  const parsed = Number(normalized);

  return Number.isFinite(parsed) ? parsed : undefined;
};

export const normalizeExcelDate = (
  value: unknown,
  { withTime = false }: { withTime?: boolean } = {},
): string | undefined => {
  if (isBlank(value)) {
    return undefined;
  }

  if (value instanceof Date) {
    const parsed = dayjs(value);

    if (!parsed.isValid()) {
      return undefined;
    }

    return withTime
      ? parsed.format('YYYY-MM-DDTHH:mm:ss')
      : parsed.format('YYYY-MM-DD');
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    const parsed = excelSerialToDayjs(value);

    if (!parsed.isValid()) {
      return undefined;
    }

    return withTime
      ? parsed.format('YYYY-MM-DDTHH:mm:ss')
      : parsed.format('YYYY-MM-DD');
  }

  const text = String(value).trim();

  const formats = withTime
    ? [
        'YYYY-MM-DDTHH:mm:ss',
        'YYYY-MM-DD HH:mm:ss',
        'DD/MM/YYYY HH:mm:ss',
        'MM/DD/YYYY HH:mm:ss',
        'DD/MM/YYYY HH:mm',
        'MM/DD/YYYY HH:mm',
      ]
    : ['YYYY-MM-DD', 'DD/MM/YYYY', 'MM/DD/YYYY'];

  for (const format of formats) {
    const parsed = dayjs(text, format, true);

    if (parsed.isValid()) {
      return withTime
        ? parsed.format('YYYY-MM-DDTHH:mm:ss')
        : parsed.format('YYYY-MM-DD');
    }
  }

  const fallback = dayjs(text);

  if (!fallback.isValid()) {
    return undefined;
  }

  return withTime
    ? fallback.format('YYYY-MM-DDTHH:mm:ss')
    : fallback.format('YYYY-MM-DD');
};

export const isExcelRowBlank = (cells: unknown[]): boolean =>
  cells.every(cell => isBlank(cell));
